import { AllocateLogItem } from "@api/allocateManagementApi";
import { RetailVsCodeFilter } from "@api/throughShipment/throughShipmentApi";
import { TRYALL_STOCK_CHANNEL_OPTION } from "@constant/AllocationChannel";
import sortRetailByPinned from "@utils/retailerUtils";
import PageTitle from "@component/PageTitle";
import useDebounce from "@hooks/useDebounce";
import { fetchRetailList } from "@redux/dealerSlice";
import { fetchAllStaffs } from "@redux/staffSlice";
import {
  fetchAllocateLogList,
  fetchSkuOptions,
  resetAllocateLog,
  setAllocateLogFilter,
} from "@redux/sharing/allocateLogSlice";
import { useAppDispatch, useSelector } from "src/store";

import { Button, DatePicker, Form, Input, Select, Table } from "antd";
import locale from "antd/es/date-picker/locale/zh_TW";
import { ColumnsType } from "antd/lib/table";
import type { Moment } from "moment";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

const Wrapper = styled.div`
  padding: 20px 20px 20px 25px;
`;

const FilterForm = styled(Form)`
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0px 22px;

  .ant-form-item-label {
    text-align: left;
  }

  .ant-input,
  .ant-select,
  .ant-picker {
    width: 100% !important;
  }

  .ant-select {
    display: block;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 13px;
  margin-top: 4px;
  justify-content: flex-end;
  align-items: center;
`;

const TableWrapper = styled.div`
  border: solid 1px #f0f0f0;
  border-radius: 2px;
  padding: 20px 10px 22px 17px;
  margin-top: 20px;
`;

interface FilterFormValues {
  sku: string[];
  productName: string;
  sourceId?: number;
  destId?: number;
  staffId?: number;
  dateRange?: [Moment | null, Moment | null] | null;
}

export default function AllocateLogPage() {
  const dispatch = useAppDispatch();
  const { list: logList, filter, isFetching, skuOptions } = useSelector((state) => state.allocateLogSlice);
  const { retailList } = useSelector((state) => state.dealerSlice);
  const { allStaffData } = useSelector((state) => state.staffs);
  const [page, setPage] = useState<number>(1);

  const [filterForm] = Form.useForm<FilterFormValues>();

  useEffect(() => {
    dispatch(fetchAllocateLogList(filter));
  }, [dispatch, filter]);

  useEffect(() => {
    dispatch(fetchRetailList({ isVs: RetailVsCodeFilter.HAS_VS_CODE }));
    dispatch(fetchAllStaffs());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetAllocateLog());
    };
  }, [dispatch]);

  const handleOnSkuSearch = useDebounce((value: string) => {
    if (value) dispatch(fetchSkuOptions(value));
  }, 300);

  const handleOnSearch = () => {
    const values = filterForm.getFieldsValue();
    setPage(1);
    dispatch(
      setAllocateLogFilter({
        ...filter,
        sku: values.sku.length > 0 ? values.sku : undefined,
        productName: values.productName || undefined,
        sourceId: values.sourceId,
        destId: values.destId,
        staffId: values.staffId,
        createdAtAfter: values.dateRange?.[0] ? values.dateRange[0].format("YYYY-MM-DD") : undefined,
        createdAtBefore: values.dateRange?.[1] ? values.dateRange[1].format("YYYY-MM-DD") : undefined,
        offset: 0,
      }),
    );
  };

  const handleOnReset = () => {
    filterForm.resetFields();
    setPage(1);
    dispatch(
      setAllocateLogFilter({
        sku: undefined,
        productName: undefined,
        sourceId: undefined,
        destId: undefined,
        createdAtAfter: undefined,
        createdAtBefore: undefined,
        staffId: undefined,
        limit: 20,
        offset: 0,
      }),
    );
  };

  const handleOnPageChange = (value: number) => {
    window.scrollTo(0, 0);
    setPage(value);
    dispatch(setAllocateLogFilter({ ...filter, offset: (value - 1) * filter.limit }));
  };

  const locationOptions = useMemo(() => {
    const retailerOptions = retailList.results.map((d) => ({ label: d.name, value: d.id }));
    return [TRYALL_STOCK_CHANNEL_OPTION, ...sortRetailByPinned(retailerOptions)];
  }, [retailList.results]);

  const columns: ColumnsType<AllocateLogItem> = [
    { key: "sku", dataIndex: "sku", title: "SKU" },
    { key: "productName", dataIndex: "productName", title: "商品名稱" },
    { key: "sourceName", dataIndex: "sourceName", title: "起始位置" },
    { key: "destName", dataIndex: "destName", title: "配貨位置" },
    { key: "staff", dataIndex: "staff", title: "操作人" },
    { key: "qty", dataIndex: "qty", title: "配貨數量" },
    { key: "createdAt", dataIndex: "createdAt", title: "操作時間" },
  ];

  return (
    <Wrapper>
      <PageTitle title="配貨紀錄" />
      <FilterForm
        form={filterForm}
        colon={false}
        labelCol={{ style: { width: 72 } }}
        initialValues={{ sku: [], productName: "" }}
        onFinish={handleOnSearch}
      >
        <FilterGrid>
          <Form.Item label="SKU" name="sku">
            <Select
              mode="tags"
              allowClear
              placeholder="輸入 SKU"
              onSearch={handleOnSkuSearch}
              tokenSeparators={[","]}
              options={skuOptions.map((sku) => ({ label: sku, value: sku }))}
            />
          </Form.Item>
          <Form.Item label="商品名稱" name="productName">
            <Input />
          </Form.Item>
          <Form.Item label="起始位置" name="sourceId">
            <Select
              showSearch
              allowClear
              placeholder="請選擇或直接輸入"
              options={locationOptions}
              filterOption={(input, option) => {
                const label = option?.label;
                return typeof label === "string" && label.toLowerCase().includes(input.toLowerCase());
              }}
            />
          </Form.Item>
          <Form.Item label="配貨位置" name="destId">
            <Select
              showSearch
              allowClear
              placeholder="請選擇或直接輸入"
              options={locationOptions}
              filterOption={(input, option) => {
                const label = option?.label;
                return typeof label === "string" && label.toLowerCase().includes(input.toLowerCase());
              }}
            />
          </Form.Item>
          <Form.Item label="時間" name="dateRange">
            <DatePicker.RangePicker locale={locale} />
          </Form.Item>
          <Form.Item label="操作人" name="staffId">
            <Select
              showSearch
              allowClear
              placeholder="請選擇或直接輸入"
              options={allStaffData.map((s) => ({ label: s.name, value: s.id }))}
              filterOption={(input, option) => {
                const label = option?.label;
                return typeof label === "string" && label.toLowerCase().includes(input.toLowerCase());
              }}
            />
          </Form.Item>
          <div />
          <ButtonRow>
            <Button onClick={handleOnReset}>清除篩選條件</Button>
            <Button type="primary" htmlType="submit">
              搜尋
            </Button>
          </ButtonRow>
        </FilterGrid>
      </FilterForm>
      <TableWrapper>
        <Table
          scroll={{ x: "max-content" }}
          loading={isFetching}
          columns={columns}
          dataSource={logList.results}
          rowKey="id"
          pagination={{
            pageSize: filter.limit,
            current: page,
            showSizeChanger: false,
            total: logList.count,
            onChange: handleOnPageChange,
          }}
        />
      </TableWrapper>
    </Wrapper>
  );
}
